import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';

/**
 * 🔴 THE BUTTON — design-system plan DS-5 (5.1; F47, F28, F13, F155, F522).
 *
 * `variant`: primary, secondary (the default), ghost, danger — OUTLINED, the documented choice (O-27; filled red only
 * inside a confirm dialog) — and link, which reads as a link and acts as a button. `size`: sm, md (the default), lg.
 *
 * `busy` is the state 72 screens wrote by hand as a label swap (`'Saving…'`). The label stays; a spinner comes in
 * front of it, `aria-busy` says so to a screen reader, and the button is `aria-disabled` rather than `disabled` — a
 * disabled button drops focus to the page's top, so a keyboard user who pressed Save would lose their place. A click
 * while busy does nothing. `icon` and `iconEnd` take an icon element; the text stays the name.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

/** The classes of a button, for an element that must look like one: a router `Link` (`ButtonLink` is the `<a>`). */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', extra?: string): string {
  return ['wb-btn', `wb-btn--${variant}`, size !== 'md' ? `wb-btn--${size}` : '', extra ?? ''].filter(Boolean).join(' ');
}

function Content({ busy, icon, iconEnd, children }: { busy?: boolean; icon?: ReactNode; iconEnd?: ReactNode; children?: ReactNode }) {
  return (
    <>
      {busy ? <span className="wb-btn__spinner" aria-hidden="true" /> : icon ? <span className="wb-btn__icon" aria-hidden="true">{icon}</span> : null}
      {children}
      {iconEnd ? <span className="wb-btn__icon" aria-hidden="true">{iconEnd}</span> : null}
    </>
  );
}

export function Button({
  variant = 'secondary',
  size = 'md',
  busy = false,
  icon,
  iconEnd,
  className,
  children,
  onClick,
  ref,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  busy?: boolean;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}) {
  return (
    <button
      type="button"
      {...props}
      ref={ref}
      className={buttonClass(variant, size, className)}
      aria-busy={busy || undefined}
      aria-disabled={busy || props['aria-disabled'] || undefined}
      onClick={busy ? (e) => e.preventDefault() : onClick}
    >
      <Content busy={busy} icon={icon} iconEnd={iconEnd}>
        {children}
      </Content>
    </button>
  );
}

/**
 * A link that looks like a button: an `<a>`, so it opens in a new tab, copies and reads as a link (F83). For a route
 * inside the app, give the router's `Link` `className={buttonClass(…)}`.
 */
export function ButtonLink({
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  className,
  children,
  ref,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
}) {
  return (
    <a {...props} ref={ref} className={buttonClass(variant, size, className)}>
      <Content icon={icon} iconEnd={iconEnd}>
        {children}
      </Content>
    </a>
  );
}
